import { useScrollAnimation } from '../../../hooks/useScrollAnimation';

export default function ExperienceTiers() {
  const { ref: headerRef, isVisible: headerVisible } = useScrollAnimation();
  const { ref: cardsRef, isVisible: cardsVisible } = useScrollAnimation();
  const { ref: patronRef, isVisible: patronVisible } = useScrollAnimation();

  const tiers = [
    {
      icon: 'ri-music-2-line',
      name: 'The Prelude Salon',
      price: '$3,500',
      description: 'An elegant introduction to the Salon experience.',
      features: [
        'One-hour curated program',
        'Solo violin, piano, or voice',
        'Personalized repertoire consultation',
        'Printed program cards',
        'Pre-concert introduction by the artist'
      ],
      ideal: 'Perfect for intimate gatherings, private dinners, and refined celebrations.'
    },
    {
      icon: 'ri-music-line',
      name: 'The Cadenza Salon',
      price: '$5,000',
      description: 'A richer chamber experience for elevated entertaining.',
      features: [
        'One-hour curated program',
        'Duo or trio ensemble',
        'Custom repertoire design',
        'Printed bespoke programs',
        'Welcome remarks + guided listening moments',
        'Post-concert artist reception'
      ],
      ideal: 'Designed for milestone birthdays, anniversary soirées, and cultivated patrons.'
    },
    {
      icon: 'ri-trophy-line',
      name: 'The Grand Salon',
      price: '$6,500',
      description: 'The full aristocratic salon revival.',
      features: [
        'One-hour premium chamber performance (trio or quartet)',
        'Fully bespoke repertoire curation',
        'Artistic direction & thematic programming',
        'Professional staging consultation',
        'Formal program booklets',
        'Champagne toast moment',
        'Private pre-event planning session'
      ],
      ideal: 'A cultural statement for those who value legacy, artistry, and distinction.'
    }
  ];

  return (
    <section id="experience" className="py-24 lg:py-32 bg-stone-100">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">

        {/* Header */}
        <div
          ref={headerRef as React.RefObject<HTMLDivElement>}
          className={`mb-16 animate-fade-up ${headerVisible ? 'in-view' : ''}`}
        >
          <p className="text-gray-600 text-sm mb-3">Four Curated Experiences</p>
          <h2 className="font-sans text-5xl lg:text-6xl font-bold text-gray-900">
            <span className="block">Choose Your</span>
            <span className="block">Salon Experience</span>
          </h2>
        </div>

        {/* Tier Cards */}
        <div
          ref={cardsRef as React.RefObject<HTMLDivElement>}
          className={`grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-6 animate-fade-up delay-200 ${cardsVisible ? 'in-view' : ''}`}
        >
          {tiers.map((tier, index) => (
            <div
              key={index}
              className={`relative rounded-2xl p-8 shadow-sm hover:shadow-lg transition-shadow duration-300 ${
                index === 1
                  ? 'bg-gray-900 text-white ring-2 ring-amber-600 shadow-xl'
                  : 'bg-stone-50'
              }`}
            >
              {index === 1 && (
                <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                  <span className="bg-amber-600 text-white text-xs font-semibold uppercase tracking-widest px-5 py-1.5 rounded-full whitespace-nowrap shadow-md">
                    Most Popular
                  </span>
                </div>
              )}
              <div className="flex items-center justify-center w-12 h-12 mb-6">
                <i className={`${tier.icon} text-4xl ${index === 1 ? 'text-amber-400' : 'text-gray-900'}`}></i>
              </div>
              <h3 className={`font-serif text-2xl font-semibold mb-3 ${index === 1 ? 'text-white' : 'text-gray-900'}`}>
                {tier.name}
              </h3>
              <div className="font-serif text-3xl text-amber-500 mb-4">{tier.price}</div>
              <p className={`mb-6 leading-relaxed ${index === 1 ? 'text-white/80' : 'text-gray-700'}`}>
                {tier.description}
              </p>
              <ul className="space-y-3 mb-6">
                {tier.features.map((feature, idx) => (
                  <li key={idx} className={`flex items-start gap-2 text-sm ${index === 1 ? 'text-white/70' : 'text-gray-600'}`}>
                    <span className="text-amber-500 mt-0.5">•</span>
                    <span>{feature}</span>
                  </li>
                ))}
              </ul>
              <p className={`text-sm italic mb-6 border-t pt-6 ${index === 1 ? 'text-white/60 border-white/20' : 'text-gray-600 border-gray-200'}`}>
                {tier.ideal}
              </p>
              <a
                href="#booking"
                className={`inline-flex items-center text-sm font-medium transition-colors cursor-pointer ${
                  index === 1 ? 'text-amber-400 hover:text-amber-300' : 'text-gray-900 hover:text-amber-700'
                }`}
              >
                Learn More
                <i className="ri-arrow-right-line ml-2"></i>
              </a>
            </div>
          ))}
        </div>

        {/* Pricing Note */}
        <p className="text-sm text-gray-500 italic text-center mb-12">
          <i className="ri-information-line mr-1.5 text-amber-500"></i>
          Note: Piano must be provided at site.
        </p>

        {/* Patron's Conservatory */}
        <div
          ref={patronRef as React.RefObject<HTMLDivElement>}
          className={`bg-gradient-to-r from-gray-900 to-gray-800 rounded-2xl p-10 lg:p-12 text-white animate-fade-up delay-300 ${patronVisible ? 'in-view' : ''}`}
        >
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-8">
            <div className="flex-1">
              <div className="inline-block bg-amber-600/20 text-amber-400 text-xs uppercase tracking-widest px-4 py-2 rounded-full mb-4">
                Invitation Only
              </div>
              <h3 className="font-serif text-3xl lg:text-4xl font-semibold mb-4">
                The Patron's Conservatory
              </h3>
              <p className="text-white/80 text-lg mb-6">For collectors, philanthropists, and cultural patrons.</p>
              <div className="grid sm:grid-cols-2 gap-4 mb-6">
                {[
                  'Multi-event seasonal programming',
                  'Priority booking',
                  'Commissioned repertoire options',
                  'Private artist dinners',
                  'Exclusive Society membership status',
                ].map((item) => (
                  <div key={item} className="flex items-start gap-2">
                    <i className="ri-star-line text-amber-400 mt-1"></i>
                    <span className="text-sm text-white/90">{item}</span>
                  </div>
                ))}
              </div>
              <p className="text-white/60 text-sm italic">Reserved for select patrons.</p>
            </div>
            <div className="lg:text-right">
              <div className="font-serif text-2xl text-amber-400 mb-4">Custom Investment</div>
              <a
                href="#booking"
                className="inline-flex items-center gap-3 bg-white text-gray-900 px-8 py-3 rounded-full font-medium hover:bg-amber-50 transition-colors whitespace-nowrap cursor-pointer"
              >
                Request Invitation
                <i className="ri-arrow-right-line"></i>
              </a>
            </div>
          </div>
        </div>

        {/* Standard Statement */}
        <div className="mt-16 text-center">
          <p className="font-serif text-lg lg:text-xl tracking-widest uppercase text-gray-800 font-semibold">
            Exclusively Conservatory-Trained Musicians. Without Exception.
          </p>
        </div>

      </div>
    </section>
  );
}
